import React, { useState, useCallback } from 'react';
import { GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';
import './MapComponent.css';
import AlertMessage from './AlertMessage';

const containerStyle = {
  width: '100%',
  height: '100%'
};

const center = {
  lat: 43.2431,
  lng: 2.1921
};

const MapComponent = ({ showCities, showStreets, showPOI, onAddCity, randomPoint, onCorrectAnswer, markers, displaySkippedPoints, isHintDisplayed }) => {
  const mapStyles = [];
  const [alert, setAlert] = useState(null);

  if (!showCities) {
    mapStyles.push({
      featureType: 'administrative.locality',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }]
    });
  }

  if (!showStreets) {
    mapStyles.push({
      featureType: 'road.local',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }]
    }, {
      featureType: 'road.arterial',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }]
    }, {
      featureType: 'road.highway',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }]
    });
  }

  if (!showPOI) {
    mapStyles.push({
      featureType: 'poi',
      stylers: [{ visibility: 'off' }]
    }, {
      featureType: 'poi.park',
      stylers: [{ visibility: 'off' }]
    }, {
      featureType: 'poi.sports_complex',
      stylers: [{ visibility: 'off' }]
    }, {
      featureType: 'poi.attraction',
      stylers: [{ visibility: 'off' }]
    }, {
      featureType: 'poi.business',
      stylers: [{ visibility: 'off' }]
    }, {
      featureType: 'transit.station.rail',
      stylers: [{ visibility: 'off' }]
    }, {
      featureType: 'transit.station.airport',
      stylers: [{ visibility: 'off' }]
    }, { 
      featureType: 'administrative.neighborhood', 
      stylers: [{ visibility: 'off' }] 
    });
  }

  const [map, setMap] = useState(null);
  const [selectedPlace, setSelectedPlace] = useState(null);

  const onLoad = useCallback(function callback(map) {
    setMap(map);
  }, []);

  const onUnmount = useCallback(function callback(map) {
    setMap(null);
  }, []);

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radius of the Earth in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      0.5 - Math.cos(dLat)/2 + 
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      (1 - Math.cos(dLon))/2;

    return R * 2 * Math.asin(Math.sqrt(a));
  };

  const handleMapClick = (event) => {
    if (!randomPoint || isHintDisplayed) { // Check if hint is displayed
      return;
    }

    const showAlert = (type, message) => {
      setAlert({ type, message });
    };
    
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    const distance = calculateDistance(lat, lng, randomPoint.lat, randomPoint.lng);
    if (distance <= 0.2) {
      onCorrectAnswer(); // Move to the next point logic
    } else {
      showAlert('wrong', 'Réponse incorrecte.');
    }
  };

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={center}
      zoom={10}
      onLoad={onLoad}
      onUnmount={onUnmount}
      options={{
        styles: mapStyles,
        fullscreenControl: false,
        mapTypeControl: false,
        streetViewControl: false,
      }}
      onClick={handleMapClick}
    >
      {markers.map((marker, index) => (
        <Marker key={index} position={{ lat: marker.lat, lng: marker.lng }} label={marker.name} />
      ))}
      {displaySkippedPoints.map((point, index) => (
        <Marker
          key={index}
          position={{ lat: point.lat, lng: point.lng }}
          icon={{
            url: 'http://maps.google.com/mapfiles/ms/icons/yellow-dot.png'
          }}
        />
      ))}
      {selectedPlace && (
        <InfoWindow
          position={{ lat: selectedPlace.geometry.location.lat(), lng: selectedPlace.geometry.location.lng() }}
          onCloseClick={() => setSelectedPlace(null)}
        >
          <div>
            <h2>{selectedPlace.name}</h2>
            <p>{selectedPlace.vicinity}</p>
          </div>
        </InfoWindow>
      )}
      {alert && (
        <AlertMessage
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
    </GoogleMap>
  );
}

export default MapComponent;
